import React, { useEffect, useRef, useState } from "react";
import { FaAngleDown, FaAngleUp } from "react-icons/fa6";
import { MdAddCircle, MdInventory2, MdStorefront, MdLanguage } from "react-icons/md";
import { useLocation, useNavigate } from "react-router-dom";
import useAuthentication from "../../auth/authHelper";
import { useDispatch, useSelector } from "react-redux";
import {
  clearState,
  setActiveButton,
} from "../../store/slices/cartSlice";
import { clearLayoutState } from "../../store/slices/layoutSlice";
import { useTranslation } from "react-i18next";
import { WR } from "../ui/tokens";

const Header = () => {
  const {t} = useTranslation();
  const { userLogout } = useAuthentication();
  const [isOpen, setIsOpen] = useState(false);
  const [selectedOption, setSelectedOption] = useState(null);
  const shopName = useSelector((state) => state?.cart?.userInfo?.shopName);
  const [isMobile, setIsMobile] = useState(window.innerWidth <= 768);
  const [displayName, setDisplayName] = useState(shopName || "@shop_name");
  const navigate = useNavigate();
  const location = useLocation();
  const dispatch = useDispatch();
  const dropdownRef = useRef(null);
  const language = localStorage.getItem("lang");

  // Which segment is highlighted is DERIVED FROM THE ROUTE, never from stored
  // state. The previous implementation read a redux-persisted `activeButton`,
  // which nothing kept in sync with the URL: any navigation into the new-order
  // flow that didn't go through this header (Create another order, back from
  // checkout, …) left "Retrieve" lit while the staff was on the product grid —
  // and localStorage made the wrong value survive reloads and shop switches.
  const RETRIEVE_PATHS = ["/retrivecheckout", "/retrievesuccess"];
  const activeTab = (() => {
    const path = location.pathname.toLowerCase().replace(/\/+$/, "") || "/";
    if (RETRIEVE_PATHS.includes(path)) return "retrieve";
    // /start and /personalinfo are shared by both flows — they carry the mode
    // in the navigation state (isRetrieve / retrieve).
    if (path === "/start") return location.state?.isRetrieve ? "retrieve" : "new";
    if (path === "/personalinfo") return location.state?.retrieve ? "retrieve" : "new";
    return "new";
  })();

  // Language switch — same mechanism as the Footer (set lang + reload).
  const handleLanguage = (e) => {
    localStorage.setItem("lang", e.target.value);
    window.location.reload();
  };

  useEffect(() => {
    const handleResize = () => setIsMobile(window.innerWidth <= 768);
    window.addEventListener("resize", handleResize);
    return () => window.removeEventListener("resize", handleResize);
  });

  // (removed) An effect with NO dependency array used to re-run on every render
  // and call handleNewButtonClick() — which NAVIGATES to /start — whenever the
  // stored activeButton was falsy. It only existed to initialise the highlight;
  // activeTab is derived from the route now, so it is dead weight and its
  // surprise navigation (it overrode navigate(-1) calls) is gone with it.

  useEffect(() => {
    const handleClickOutside = (event) => {
      if (dropdownRef.current && !dropdownRef.current.contains(event.target)) {
        setIsOpen(false);
      }
    };
    document.addEventListener("mousedown", handleClickOutside);
    return () => document.removeEventListener("mousedown", handleClickOutside);
  });

  useEffect(() => {
    setDisplayName(selectedOption || shopName || "@shop_name");
  }, [selectedOption, shopName]);

  useEffect(() => {
    if (location.state?.displayName) {
      setDisplayName(location.state.displayName);
    } else {
      setDisplayName(shopName || "@shop_name");
    }
  }, [location]);

  const toggleDropdown = () => {
    setIsOpen(!isOpen);
  };

  const handleNewButtonClick = () => {
    setSelectedOption(shopName);
    dispatch(setActiveButton("new"));
    navigate("/start");
  };

  const handleRetrieveButtonClick = () => {
    setSelectedOption(shopName);
    dispatch(setActiveButton("retrieve"));
    navigate("/start", { state: { isRetrieve: true } });
  };

  const handleLogout = () => {
    userLogout(navigate);
    dispatch(clearState());
    dispatch(clearLayoutState());
    dispatch(setActiveButton(null));
  };

  const handleLogoClick = () => {
    navigate("/start");
  };

  const handleOptionClick = (option) => {
    setSelectedOption(option);
    dispatch(setActiveButton(option));
    setIsOpen(false);
  };

  const handlePrinterSettingsClick = () => {
    handleOptionClick("Printer settings");
    navigate("/printerSetting", { state: { displayName: "Printer settings" } });
  };
  const handleTransaction = () => {
    handleOptionClick("Checkout & transactions");
    navigate("/checkoutTransactions", { state: { displayName: "Checkout & transactions" } });
  };

  const segStyle = (active, accent, inactive) => ({
    display: "inline-flex",
    alignItems: "center",
    gap: 8,
    padding: "10px 22px",
    borderRadius: 999,
    fontSize: 14,
    fontWeight: 700,
    letterSpacing: -0.1,
    border: 0,
    cursor: "pointer",
    background: active ? accent : "transparent",
    color: active ? "#fff" : inactive,
    boxShadow: active ? `0 6px 14px -6px ${accent}` : "none",
    transition: "all .18s ease",
  });

  const menuItem = {
    padding: "10px 12px",
    borderRadius: 10,
    fontSize: 14,
    fontWeight: 600,
    color: WR.color.encre,
    cursor: "pointer",
    whiteSpace: "nowrap",
  };

  return (
    <header
      style={{
        position: "sticky",
        top: 0,
        zIndex: 40,
        background: "#fff",
        boxShadow: "0 8px 28px -20px rgba(61,69,134,.55)",
        fontFamily: WR.font.sans,
      }}
    >
      <div style={{ height: 3, width: "100%", background: activeTab === "retrieve" ? WR.color.mintDeep : WR.color.brand, transition: "background .2s ease" }} />
      <div
        style={{
          display: "flex",
          alignItems: "center",
          gap: 24,
          padding: "12px clamp(18px,3.5vw,40px)",
        }}
      >
        {/* Left — logo + brand */}
        <div
          style={{ display: "flex", alignItems: "center", gap: 12, minWidth: 200, cursor: "pointer" }}
          onClick={handleLogoClick}
          role="button"
        >
          <div
            style={{
              width: 44,
              height: 44,
              borderRadius: 999,
              background: "#EEF1FB",
              display: "flex",
              alignItems: "center",
              justifyContent: "center",
              border: `1.5px solid ${WR.color.border}`,
              overflow: "hidden",
              flexShrink: 0,
            }}
          >
            <img src="../assets/imgs/logo_rond 1.svg" alt="" style={{ width: "100%", height: "100%", objectFit: "contain" }} />
          </div>
          <div style={{ display: "flex", flexDirection: "column", gap: 1, minWidth: 0 }}>
            <span style={{ fontSize: 14, fontWeight: 800, letterSpacing: -0.2, color: WR.color.encre, whiteSpace: "nowrap" }}>
              {shopName || "@shop_name"}
            </span>
            <span style={{ fontSize: 10, fontWeight: 700, letterSpacing: 1.4, textTransform: "uppercase", color: WR.color.inkSoft }}>
              powered by WOSH
            </span>
          </div>
        </div>

        {/* Center — New / Retrieve toggle */}
        <div style={{ flex: 1, display: "flex", justifyContent: "center" }}>
          <div
            style={{
              display: "inline-flex",
              alignItems: "center",
              gap: 4,
              background: WR.color.cardMuted,
              padding: 5,
              borderRadius: 999,
              border: `1px solid ${WR.color.hairline}`,
            }}
          >
            <button onClick={handleNewButtonClick} style={segStyle(activeTab === "new", WR.color.brand, WR.color.inkMuted)}>
              <MdAddCircle size={17} />
              {t("NEW_ORDER")}
            </button>
            <button onClick={handleRetrieveButtonClick} style={segStyle(activeTab === "retrieve", WR.color.mintDeep, WR.color.mintDeep)}>
              <MdInventory2 size={17} />
              {t("RETRIEVE")}
            </button>
          </div>
        </div>

        {/* Right — shop menu + language */}
        <div style={{ display: "flex", alignItems: "center", gap: 10, minWidth: 200, justifyContent: "flex-end" }}>
          <div ref={dropdownRef} style={{ position: "relative" }}>
            <button
              onClick={toggleDropdown}
              style={{
                display: "inline-flex",
                alignItems: "center",
                gap: 8,
                padding: "9px 14px",
                borderRadius: 999,
                background: WR.color.lavSoft,
                color: WR.color.brand,
                fontSize: 13,
                fontWeight: 700,
                border: `1px solid ${WR.color.border}`,
                cursor: "pointer",
              }}
            >
              <MdStorefront size={16} />
              <span style={{ maxWidth: 150, overflow: "hidden", textOverflow: "ellipsis", whiteSpace: "nowrap" }}>
                {displayName}
              </span>
              {isOpen ? <FaAngleUp size={13} style={{ opacity: 0.7 }} /> : <FaAngleDown size={13} style={{ opacity: 0.7 }} />}
            </button>
            {isOpen && (
              <div
                style={{
                  position: "absolute",
                  right: 0,
                  top: "calc(100% + 8px)",
                  background: "#fff",
                  borderRadius: 14,
                  boxShadow: WR.shadow.float,
                  border: `1px solid ${WR.color.border}`,
                  minWidth: 230,
                  overflow: "hidden",
                  zIndex: 50,
                }}
              >
                <ul style={{ listStyle: "none", margin: 0, padding: 6 }}>
                  <li onClick={handleTransaction} style={menuItem}>
                    {t("CHECKOUT_&_TRANSACTION")}
                  </li>
                  <li onClick={handlePrinterSettingsClick} style={menuItem}>
                    Printer settings
                  </li>
                  <li onClick={handleLogout} style={{ ...menuItem, color: WR.color.danger }}>
                    Sign out
                  </li>
                </ul>
              </div>
            )}
          </div>

          {/* Language globe — same mechanism as the Footer */}
          <div style={{ position: "relative", width: 40, height: 40, flexShrink: 0 }}>
            <span
              aria-hidden="true"
              style={{
                position: "absolute",
                inset: 0,
                borderRadius: 999,
                background: "#fff",
                border: `1px solid ${WR.color.border}`,
                display: "flex",
                alignItems: "center",
                justifyContent: "center",
                color: WR.color.inkMuted,
              }}
            >
              <MdLanguage size={18} />
            </span>
            <select
              value={language || "english"}
              onChange={handleLanguage}
              aria-label="Language"
              style={{
                position: "absolute",
                inset: 0,
                width: 40,
                height: 40,
                opacity: 0,
                cursor: "pointer",
                border: 0,
              }}
            >
              <option value="english">English</option>
              <option value="french">French</option>
              <option value="dutch">Dutch</option>
            </select>
          </div>
        </div>
      </div>
    </header>
  );
};

export default Header;
